// use client: selectedButton (platinum/signature) plus the usePersistantStore
// read of `creditCardData`. CLAUDE.md ladder level 3 — the whole view is
// genuinely interactive, so a root directive is justified here:
//   * selectedButton drives every child (landing hero, both product branches,
//     the calculator's cardType/cashback props and the closing LetsConnect
//     title) — there is no isolatable interactive subtree.
//   * all eight child sections are already client components, so extracting a
//     leaf would leave a server parent that renders nothing server-side.
//   * the "render both variants, toggle with CSS" pattern is unsafe here: each
//     branch carries unique DOM ids (card-calculator, card-eligibility,
//     credit-card-landing, lets-connect) and calculator input ids
//     (currentBalance, annualProfitRate, minMonthlyPayment). Duplicating them
//     would emit invalid HTML and break <label for> associations.
'use client'

import type { CardType } from '@/sections/CreditCard/types'

import '../../digital-banking/digital-banking.css'
import './Cards.css'

import { ASSETS_BASE_URL } from '@/constants'
import { platinumCreditCard, signatureCreditCard } from '@/constants/cards'

import { useEffect, useState } from 'react'

import GetMyalrajhi from '@/sections/ComingSoon/GetMyalrajhi'
import LetsConnect from '@/sections/ComingSoon/LetsConnect'
import { CardCalculator } from '@/sections/CreditCard/CardCalculator'
import CardEligibility from '@/sections/CreditCard/CardEligibility'
import { CardFeesAndCharges } from '@/sections/CreditCard/CardFeesAndCharges'
import CardLetsConnect from '@/sections/CreditCard/CardLetsConnect'
import CardFeatures from '@/sections/CreditCard/CardsFeatures'
import CreditCardLanding from '@/sections/CreditCard/CreditCardLanding'
import { usePersistantStore } from '@/stores/Store'

const PlatinumImage = `${ASSETS_BASE_URL}assets/files/visa_platinum_cc_shadow_70a958754a.svg`
const SignatureImage = `${ASSETS_BASE_URL}assets/files/visa_signature_cc_shadow_1de044c4ad.svg`

const PlatinumTableImage = `${ASSETS_BASE_URL}assets/files/Premium_Application_Fee_Table_July_2026_497237ebbf.svg`
const SignatureTableImage = `${ASSETS_BASE_URL}assets/files/Signature_Application_Fee_Table_a5d9765412.svg`

const ImageComponent = ({ img }) => (
  <div className="credit-card-image-container">
    <img src={img} alt="Credit Card Image" width="100%" height="auto" />
  </div>
)

/**
 * The card switcher's tabs. The state value is declared alongside its label
 * rather than derived from it: `label.toLocaleLowerCase()` is locale-sensitive
 * (in a Turkish locale 'I' lowercases to 'ı'), which would silently stop
 * matching `CardType`.
 */
const CARD_TABS: { label: string; value: CardType }[] = [
  { label: 'Platinum', value: 'platinum' },
  { label: 'Signature', value: 'signature' }
]

function CreditCards() {
  const { creditCardData } = usePersistantStore()
  const [selectedButton, setSelectedButton] = useState<CardType>('platinum')

  const platinumCardEligibility = {
    type: 'pf',
    steps: 2,
    title: 'Eligibility and required documents',
    documentsTitle: 'Required documents',
    employeeType: 'Salaried employees',
    tagline2: 'minimum monthly income',
    eligibilityTitle: 'Eligibility',
    yearsOld: 'years old',
    documents: [
      {
        title: '<span>Salaried employees</span>',
        content: `
      <ul>
        <li>Photocopy of <b>IC / Identification document</b></li>
        <li>Latest <b>12 months’ EPF statements</b></li>
      </ul>
    `,
        backgroundColor: '#DCE9FF'
      },
      {
        title: '<span>Self-employed</span>',
        content: `
      <ul>
        <li>Photocopy of <b>IC / Identification document</b></li>
        <li>Latest <b>6 months’ of your company bank statements</b></li>
        <li><b>Business registration form</b></li>
        <li>
          <b>Personal or business income tax returns</b> for the last 2 years,
          with proof of tax payments
        </li>
      </ul>
    `,
        backgroundColor: '#F2F4F7'
      }
    ],
    ageRange: {
      minimum: {
        value: '21',
        label: 'Minimum age'
      },
      maximum: {
        value: '65',
        label: 'Maximum age'
      }
    },
    income: {
      range: 'RM24,000',
      subheading: 'Minimum annual income:'
    }
  }
  const signatureCardEligibility = {
    type: 'pf',
    steps: 2,
    title: 'Eligibility and required documents',
    documentsTitle: 'Required documents',
    employeeType: 'Salaried employees',
    tagline2: 'minimum monthly income',
    eligibilityTitle: 'Eligibility',
    yearsOld: 'years old',
    documents: [
      {
        title: '<span>Salaried employees</span>',
        content: `
      <ul>
        <li>Photocopy of <b>IC / Identification document</b></li>
        <li>Latest <b>12 months’ EPF statements</b></li>
      </ul>
    `,
        backgroundColor: '#DCE9FF'
      },
      {
        title: '<span>Self-employed</span>',
        content: `
      <ul>
        <li>Photocopy of <b>IC / Identification document</b></li>
        <li>Latest <b>6 months’ of your company bank statements</b></li>
        <li><b>Business registration form</b></li>
        <li>
          <b>Personal or business income tax returns</b> for the last 2 years,
          with proof of tax payments
        </li>
      </ul>
    `,
        backgroundColor: '#F2F4F7'
      }
    ],
    ageRange: {
      minimum: {
        value: '21',
        label: 'Minimum age'
      },
      maximum: {
        value: '65',
        label: 'Maximum age'
      }
    },
    income: {
      range: 'RM60,000',
      subheading: 'Minimum annual income:'
    }
  }

  useEffect(() => {
    if (creditCardData === 'platinum' || creditCardData === 'signature') {
      setSelectedButton(creditCardData)
    }
  }, [creditCardData])

  return (
    <div id="credit-card">
      <CreditCardLanding cardType={selectedButton} />
      <section
        className="section jumbotron"
        data-aos="zoom-in"
        data-aos-delay="200"
        style={{ paddingBottom: '0' }}
      >
        <div className="container">
          <div className="row">
            <div className="toggle-slider-container">
              <div
                className="toggle-slider"
                style={{
                  transform: `translateX(${selectedButton === 'platinum' ? '0%' : '100%'})`
                }}
              />
              {CARD_TABS.map(({ label, value }) => (
                <div
                  key={value}
                  className={`sliding-button`}
                  style={{
                    color: selectedButton === value ? '#FFFFFF' : 'var(--alrajhi-black)',
                    fontWeight: selectedButton === value ? 700 : 500
                  }}
                  onClick={() => {
                    setSelectedButton(value)
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {selectedButton === 'platinum' ? (
        // Card is Platinum
        <>
          <CardLetsConnect
            title={`alrajhi bank Malaysia<br><span className="jumbotron-subtitle-2">Visa Platinum <span className="no-wrap">Credit Card-<em>i</em></span></span>`}
            buttonText={'Let’s connect'}
            subText={
              <span style={{ color: 'var(--grey-500-body-text)' }}>
                <b>Rewarding yourself should be simple.</b> With the alrajhi bank Malaysia Visa
                Platinum Credit Card-𝘪 you get rewarded for your everyday spending. Enjoy cashback
                on what matters most, with zero annual fees for life.
              </span>
            }
            sideComponent={<ImageComponent img={PlatinumImage} />}
          />
          <CardFeatures title="Everyday rewards. Zero annual fees." data={platinumCreditCard} />
          <CardCalculator
            title="Calculate your credit card cashback and estimate your monthly payments&mdash;all in one place."
            subtext=""
            premiumCashback={5}
            generalCashback={0.2}
            maxCap={30}
            cardType={selectedButton}
          />
          <CardEligibility {...platinumCardEligibility} />
          <CardFeesAndCharges
            cardType={selectedButton}
            title="Applicable fees and charges"
            subtext="Stay informed with a simple breakdown of the card’s fees and charges."
            imgSrc={PlatinumTableImage}
          />
        </>
      ) : (
        // Card is Signature
        <>
          <CardLetsConnect
            title={`alrajhi bank Malaysia<br><span className="jumbotron-subtitle-2">Visa Signature <span className="no-wrap">Credit Card-<em>i</em></span></span>`}
            buttonText={'Let’s connect'}
            subText={
              <span style={{ color: 'var(--grey-500-body-text)' }}>
                <b>Experience elevated rewards, wherever life takes you.</b> Earn up to 10% cashback
                on overseas spend, complimentary Priority Pass lounge visits and 24/7 Visa concierge
                service — all with the alrajhi bank Malaysia Visa Signature Credit Card-𝘪.
              </span>
            }
            sideComponent={<ImageComponent img={SignatureImage} />}
          />
          <CardFeatures title="Elevate your lifestyle" data={signatureCreditCard} />
          <CardCalculator
            title="Calculate your credit card cashback and estimate your monthly payments&mdash;all in one place."
            subtext=""
            premiumCashback={10}
            generalCashback={0.6}
            maxCap={80}
            cardType={selectedButton}
          />
          <CardEligibility {...signatureCardEligibility} />
          <CardFeesAndCharges
            cardType={selectedButton}
            title="Applicable fees and charges"
            subtext="Stay informed with a simple breakdown of the card’s fees and charges."
            imgSrc={SignatureTableImage}
          />
        </>
      )}
      <GetMyalrajhi heading="Manage your credit cards on MY alrajhi" />
      <LetsConnect
        title={
          selectedButton === 'signature'
            ? 'Have questions about the Visa Signature Credit Card-<i>i</i> ?'
            : 'Have questions about the Visa Platinum Credit Card-<i>i</i> ?'
        }
        subtitle="Submit your details and we will be in touch soon."
      />
    </div>
  )
}

export default CreditCards
